import { useCallback, useRef, useState } from "react"

import type { BoxProps, StackProps } from "@mui/material"
import { Backdrop, Box, Fade, Modal, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { find, reject, toLower } from "lodash"

import type { ButtonProps, FC, FCWC, ResponsiveImageType } from ".."
import { Button, ContainerStack, IconButton, ResponsiveImage, SvgIcon } from ".."
import type { LinkType } from "../../../src/graphql/types"

const getDirection = (direction: "left" | "right") => {
  if (direction === "right") {
    return (i: number) => (i % 2 === 0 ? "right" : "left")
  } else {
    return (i: number) => (i % 2 === 0 ? "left" : "right")
  }
}

type CTAColors = "blue" | "yellow" | "white"
type CTADirection = "left" | "right"

type StackedCTAItem = {
  title: string
  description: string
  image?: ResponsiveImageType
  link?: LinkType
  // Need to be string due to Drupal send camel case and need lowercasing.
  color?: string
  video?: string
  modal_video?: string
}

type StackedCTAProps = {
  direction?: CTADirection
  items: StackedCTAItem[]
}

const StackedCTA: FC<StackedCTAProps> = ({ direction = "right", items }) => {
  return (
    <ContainerStack>
      {items.map((item, i) => (
        <StackedCTACard
          key={i}
          {...item}
          color={toLower(item.color || "")}
          direction={getDirection(direction)(i)}
        />
      ))}
    </ContainerStack>
  )
}

export default StackedCTA

/** A sub-component for the individual CTA item */
type CardProps = StackedCTAItem & { direction: CTADirection }
const StackedCTACard: FC<CardProps> = ({
  color = "white",
  direction = "right",
  title,
  description,
  link,
  modal_video,
  image,
  video,
}) => {
  const isLinkAvailable = Boolean(link?.title)
  // Compose image variants without original default size image and with extra
  // medium breakpoint that clone from 3xl so we avoid pixelation in medium screens.
  const imageVariants = reject(image?.variants, { style: "default" }) || []
  const smallImageVariant = find(imageVariants, { style: "sm" })
  const xlImageVariant = find(imageVariants, { style: "3xl" })
  const defaultImageVariant = smallImageVariant || xlImageVariant
  const augmentedImageVariants = {
    variants: imageVariants,
  }

  if (xlImageVariant) {
    augmentedImageVariants.variants.push({ ...xlImageVariant, style: "md" })
  }

  if (defaultImageVariant) {
    augmentedImageVariants.variants.push({ ...defaultImageVariant, style: "default" })
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
      py={{ xs: 2, md: 2.5 }}
      gap={{ xs: 3, md: 5 }}
    >
      <StackedCTAMedia
        order={{ xs: 1, md: direction === "right" ? 1 : 2 }}
        image={augmentedImageVariants}
        video={video}
      />
      {/* <StackedCTAImage order={{ xs: 1, md: direction === "right" ? 1 : 2 }} image={image} /> */}
      <StackedCTAContent
        contentColor={color}
        order={{ xs: 2, md: direction === "right" ? 2 : 1 }}
        bgcolor={(theme) =>
          theme.palette[
            color === "blue" ? "secondary.200" : color === "yellow" ? "tertiary.200" : "neutral.100"
          ].main
        }
        color={(theme) => theme.palette[color === "blue" ? "secondary.800" : "neutral.700"].main}
      >
        <StackedCTATitle>{title}</StackedCTATitle>
        <StackedCTADescription description={description} />
        {modal_video ? (
          <StackedCTAModal color={color} modal_video={modal_video}>
            Play Video
          </StackedCTAModal>
        ) : (
          isLinkAvailable && (
            <StackedCTALink color={color} href={link?.uri}>
              {link?.title}
            </StackedCTALink>
          )
        )}
      </StackedCTAContent>
    </Box>
  )
}

/** A sub-component for the image in a CTA item */
type MediaProps = Pick<StackedCTAItem, "image" | "video"> & BoxProps
const StackedCTAMedia: FC<MediaProps> = ({ image, video, ...props }) => (
  <Box
    overflow="hidden"
    borderRadius={1.5}
    position="relative"
    sx={{ aspectRatio: "1" }}
    {...props}
  >
    {video ? (
      <StackedCTAVideo video={video} />
    ) : (
      image && (
        <ResponsiveImage image={image} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      )
    )}
  </Box>
)
type VideoProps = Pick<StackedCTAItem, "video"> & BoxProps
const StackedCTAVideo: FC<VideoProps> = ({ video }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const playPause = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }, [videoRef.current])

  return (
    <>
      <Box
        ref={videoRef}
        component="video"
        autoPlay
        muted
        loop
        width="100%"
        height="100%"
        position="relative"
        zIndex={1}
        sx={{ aspectRatio: "1", objectFit: "cover" }}
      >
        <source src={video} type="video/mp4" />
      </Box>

      <IconButton
        icon={
          <SvgIcon
            variant={isPlaying ? "pause" : "play"}
            size={12}
            sx={{ fill: (theme) => theme.palette["neutral.100"].main }}
          />
        }
        aria-label="Play Pause button"
        onClick={playPause}
        sx={(theme) => ({
          "position": "absolute",
          "zIndex": 4,
          "right": theme.spacing(3),
          "bottom": theme.spacing(3),
          "backgroundColor": alpha(theme.palette["neutral.900"].main, 0.05),
          "border": `1px solid ${alpha(theme.palette["neutral.100"].main, 0.2)}`,
          "backdropFilter": "blur(4px)",

          "&:hover, &:focus-visible": {
            backgroundColor: alpha(theme.palette["neutral.900"].main, 0.4),
          },
        })}
      />
    </>
  )
}

/** A sub-component for the image in a CTA item */
type ImageProps = Pick<StackedCTAItem, "image"> & BoxProps
const StackedCTAImage: FC<ImageProps> = ({ image, ...props }) => (
  <Box overflow="hidden" borderRadius={1.5} {...props}>
    {image && (
      <ResponsiveImage image={image} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
    )}
  </Box>
)

/** A sub-component for the content in a CTA item */
type ContentProps = StackProps & { contentColor: string }
const StackedCTAContent: FC<ContentProps> = ({ contentColor, children, ...props }) => (
  <Stack
    height="100%"
    px={{ xs: contentColor === "white" ? 3 : 5, md: 10 }}
    py={{ xs: contentColor === "white" ? 5 : 7, md: 10 }}
    alignItems="center"
    justifyContent="center"
    borderRadius={1.5}
    {...props}
  >
    <Box maxWidth={{ md: 490 }}>{children}</Box>
  </Stack>
)

/** A sub-component for the title in a CTA item */
const StackedCTATitle: FCWC = ({ children }) => (
  <Typography variant="heading.xl" component="h2" mb={{ xs: 8, md: 10 }}>
    {children}
  </Typography>
)

/** A sub-component for the description in a CTA item */
type DescriptionProps = Pick<StackedCTAItem, "description">
const StackedCTADescription: FC<DescriptionProps> = ({ description }) => (
  <Typography
    variant="body.md"
    component="div"
    mb={{ xs: 8, md: 10 }}
    dangerouslySetInnerHTML={{ __html: description }}
  />
)

/** A sub-component for the link button in a CTA item */
type CTABtnProps = ButtonProps & Pick<StackedCTAItem, "modal_video"> & { color: string }
const StackedCTAModal: FC<CTABtnProps> = ({ modal_video, color = "white", ...props }) => {
  const [open, setOpen] = useState(false)
  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <>
      <Button
        variant={
          color === "blue"
            ? "neutral.secondary"
            : color === "yellow"
            ? "neutral.tertiary"
            : "secondary"
        }
        sx={(theme) => ({
          [theme.breakpoints.down("md")]: {
            display: "flex",
            textAlign: "center",
            width: "100%",
          },
        })}
        onClick={handleOpen}
        {...props}
      />
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
              filter: "drop-shadow(2px 2px 20px rgba(38, 54, 84, 0.10))",
            },
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Box
            sx={(theme) => ({
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              maxWidth: 1000,
              px: 5,
            })}
          >
            <Box
              component="div"
              sx={{
                aspectRatio: "16/9",
                borderRadius: 1.5,
                overflow: "hidden",

                iframe: {
                  width: "100%!important",
                  height: "100%!important",
                  display: "block",
                  m: 0,
                },
              }}
              dangerouslySetInnerHTML={{ __html: modal_video! }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  )
}

const StackedCTALink: FC<CTABtnProps> = ({ color = "white", href, ...props }) => (
  <Button
    variant={
      color === "blue" ? "neutral.secondary" : color === "yellow" ? "neutral.tertiary" : "secondary"
    }
    sx={(theme) => ({
      [theme.breakpoints.down("md")]: {
        display: "flex",
        textAlign: "center",
        width: "100%",
      },
    })}
    href={href}
    {...props}
  />
)
